import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useBookCatalog from '../utils/useBookCatalog';
import BookCard from '../components/BookCard';
import TagBar from '../components/TagBar';
import { TAGS } from '../data/homeBooks';
import { useCategories, categorySlug } from '../utils/categories';
import { useGenres } from '../utils/genres';
import '../styles/gallery.css';
import '../styles/profile.css';
import '../styles/category.css';

const LANG_OPTIONS = [
  { id: 'all', label: 'All languages' },
  { id: 'en',  label: 'English' },
  { id: 'ar',  label: 'Arabic' },
];
const GENRE_ALL_OPTION = { id: 'all', label: 'All genres' };
const FORMAT_OPTIONS = [
  { id: 'all',       label: 'Any format' },
  { id: 'landscape', label: 'Landscape' },
  { id: 'portrait',  label: 'Portrait' },
];
const QUIZ_OPTIONS = [
  { id: 'all', label: 'Quiz: any' },
  { id: 'yes', label: 'Has quiz' },
  { id: 'no',  label: 'No quiz' },
];
// Keep each fetched batch on a full grid row at the catalogue breakpoints.
const catalogBatchSizeForViewport = () => {
  if (typeof window === 'undefined') return 50;
  if (window.innerWidth <= 824) return 54; // two or three columns
  if (window.innerWidth <= 1064) return 52; // four columns
  return 50; // five columns
};
const LEVEL_OPTIONS = [
  { id: 'all', label: 'All reading levels' },
  ...['Dawn', 'Lantern', 'Compass', 'Beacon', 'Horizon'].map(level => ({ id: level, label: level })),
];

const SORTS = [
  { id: 'relevance', label: 'Relevance' },
  { id: 'newest',    label: 'Newest' },
  { id: 'title',     label: 'Title A–Z' },
  { id: 'readingLevel', label: 'Reading level' },
];

function FilterChip({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const active = options.find((o) => o.id === value);
  const isSet = value && value !== 'all';
  return (
    <div className="category-sort" ref={ref} style={{ display: 'inline-block' }}>
      <button
        type="button"
        className={`category-filter-chip ${isSet ? 'is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {isSet ? active.label : label}
        <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" style={{ marginLeft: 6 }}>
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="category-sort-menu" role="menu">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitem"
              className={`category-sort-item ${value === o.id ? 'is-active' : ''}`}
              onClick={() => { onChange(o.id); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CategoryPage() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const customCategories = useCategories();
  const adminGenres = useGenres();
  const genreOptions = useMemo(
    () => [GENRE_ALL_OPTION, ...adminGenres.map((g) => ({ id: g.name, label: g.name }))],
    [adminGenres]
  );

  const allTags = useMemo(() => {
    const builtinSlugs = new Set(TAGS.map((t) => t.id.toLowerCase()));
    const builtinLabels = new Set(TAGS.map((t) => t.label.toLowerCase()));
    const extras = customCategories
      .filter((c) => c?.name)
      .filter((c) => {
        const slug = categorySlug(c.name);
        const label = c.name.toLowerCase();
        return !builtinSlugs.has(slug) && !builtinLabels.has(label);
      })
      .map((c) => ({
        id: `cat-${categorySlug(c.name)}`,
        label: c.name,
        glow: 'gold',
        category: c.name,
      }));
    return [...TAGS, ...extras];
  }, [customCategories]);

  const validTag = allTags.find((t) => t.id === tag) ? tag : 'all';
  const activeCustomCat = allTags.find((t) => t.id === validTag)?.category;

  const [sort, setSort] = useState('relevance');
  const [sortOpen, setSortOpen] = useState(false);
  const [view, setView] = useState('grid');

  const [langFilter, setLangFilter] = useState('all');
  const [genreFilter, setGenreFilter] = useState('all');
  const [quizFilter, setQuizFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [query, setQuery] = useState('');

  const filters = useMemo(() => ({ tag: validTag, category: activeCustomCat, sort, language: langFilter, genre: genreFilter, quiz: quizFilter, format: formatFilter, readingLevel: levelFilter, q: query }), [validTag, activeCustomCat, sort, langFilter, genreFilter, quizFilter, formatFilter, levelFilter, query]);
  const [catalogBatchSize, setCatalogBatchSize] = useState(catalogBatchSizeForViewport);
  useEffect(() => {
    const updateBatchSize = () => setCatalogBatchSize(catalogBatchSizeForViewport());
    window.addEventListener('resize', updateBatchSize);
    return () => window.removeEventListener('resize', updateBatchSize);
  }, []);
  const { items: apiBooks, total: catalogTotal, nextOffset, busy: catalogBusy, error: catalogError, loadMore: showMore, showLess, retry } = useBookCatalog(filters, catalogBatchSize);
  const books = apiBooks;
  const activeLabel = allTags.find(t => t.id === validTag)?.label || 'All';

  const activeSortLabel = SORTS.find((s) => s.id === sort)?.label || 'Relevance';

  return (
    <div className="category-page">
      <div className="category-topbar">
        <Link to="/" className="category-logo">Deenlit</Link>
        <Link to="/" className="category-back">&#8592; Library</Link>
      </div>

      <div className="category-tagbar-wrap">
        <TagBar
          tags={allTags}
          active={validTag}
          onChange={(id) => navigate(`/category/${id}`)}
          query={query}
          onQueryChange={setQuery}
        />
      </div>

      <div className="category-filters">
        <FilterChip label="Reading Level" options={LEVEL_OPTIONS} value={levelFilter} onChange={setLevelFilter} />
        <FilterChip label="Genre" options={genreOptions} value={genreFilter} onChange={setGenreFilter} />
        <FilterChip label="Format" options={FORMAT_OPTIONS} value={formatFilter} onChange={setFormatFilter} />
        <FilterChip label="Language" options={LANG_OPTIONS} value={langFilter} onChange={setLangFilter} />
        <FilterChip label="Quiz" options={QUIZ_OPTIONS} value={quizFilter} onChange={setQuizFilter} />
        {(levelFilter !== 'all' || genreFilter !== 'all' || formatFilter !== 'all' || langFilter !== 'all' || quizFilter !== 'all') && (
          <button
            type="button"
            className="category-filter-reset"
            onClick={() => {
              setGenreFilter('all');
              setLevelFilter('all');
              setFormatFilter('all');
              setLangFilter('all');
              setQuizFilter('all');
            }}
          >
            Reset
          </button>
        )}
      </div>

      <div className="category-toolbar">
        <h2 className="category-heading">
          {activeLabel} <span className="category-count">({catalogTotal})</span>
        </h2>
        <div className="category-tools">
          <div className="category-sort">
            <button
              type="button"
              className="category-sort-btn"
              onClick={() => setSortOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={sortOpen}
            >
              {activeSortLabel}
              <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {sortOpen && (
              <div className="category-sort-menu" role="menu">
                {SORTS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="menuitem"
                    className={`category-sort-item ${sort === s.id ? 'is-active' : ''}`}
                    onClick={() => { setSort(s.id); setSortOpen(false); }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="category-view-toggle" role="group" aria-label="View">
            <button
              type="button"
              className={`category-view-btn ${view === 'grid' ? 'is-active' : ''}`}
              onClick={() => setView('grid')}
              aria-label="Grid view"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" />
                <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" />
                <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" />
                <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" />
              </svg>
            </button>
            <button
              type="button"
              className={`category-view-btn ${view === 'list' ? 'is-active' : ''}`}
              onClick={() => setView('list')}
              aria-label="List view"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <rect x="3" y="5"  width="18" height="2.4" rx="1" fill="currentColor" />
                <rect x="3" y="11" width="18" height="2.4" rx="1" fill="currentColor" />
                <rect x="3" y="17" width="18" height="2.4" rx="1" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {catalogError && <p role="alert">{catalogError} <button className="category-filter-chip" onClick={retry}>Retry loading books</button></p>}
      {catalogBusy && books.length === 0 && <p role="status">Loading books...</p>}
      {books.length === 0 && !catalogBusy ? (
        <div className="category-empty">No books match these filters.</div>
      ) : (
        <div className={`category-grid ${view === 'list' ? 'is-list' : ''}`}>
          {books.map((b, i) => (
            <BookCard key={b._id} book={b} index={i} lang={langFilter === 'ar' ? 'ar' : 'en'} />
          ))}
        </div>
      )}
      {(nextOffset !== null || books.length > catalogBatchSize) && <div className="category-load-more">
        {nextOffset !== null && <button type="button" className="category-page-action" disabled={catalogBusy} onClick={showMore}>{catalogBusy ? 'Loading books...' : 'Show more books'}</button>}
        {books.length > catalogBatchSize && <button type="button" className="category-page-action category-page-action--secondary" disabled={catalogBusy} onClick={showLess}>Show less</button>}
        <span>{books.length} of {catalogTotal} books</span>
      </div>}
    </div>
  );
}
